/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { eqString } from 'fp-ts/lib/Eq';
import { none, Option, some } from 'fp-ts/lib/Option';
import { Coordinate } from 'ol/coordinate';
import { ChangeEvent, KeyboardEvent } from 'react';
import { getAlias, getLang } from '../app';
import {
    DIV,
    INPUT,
    NodeOrOptional,
    SPAN,
    STRONG,
} from '../components/elements';
import { renderSelect } from '../components/input';
import { isENTER } from '../components/keycodes';
import {
    clearServiceResponse,
    selectService,
    updateTerm,
} from '../geocoder/events';
import { queryService } from '../geocoder/index';
import {
    LayerResult,
    ServiceDescription,
    ServiceResponse,
    serviceMessageToString,
    TermResult,
} from '../geocoder/io';
import {
    getAllServices,
    getSelectedService,
    getServiceName,
    getServicePlaceholder,
    getServiceResponse,
    getTerm,
} from '../geocoder/queries';
import { fromRecord, toTranslated, tr, Translated } from '../locale';
import { IMapViewData, Interaction, viewEventsFactory } from '../map';
import { Getter, Setter } from '../shape/types';
import { getMessageRecord } from '../source';
import { noop } from '../util';
import { makeIcon } from './button';
import { tryNameToString } from './button/names';
import { MetadataGetter } from './legend';
import { Tooltip } from './tooltip';

const coord = (result: TermResult): [number, number] => [
    result.coord.x,
    result.coord.y,
];

export type TermAction = (result: TermResult) => NodeOrOptional;

const putMark = (
    coordinates: Coordinate,
    setInteraction: Setter<Interaction>,
) =>
    setInteraction(() => ({
        label: 'mark',
        state: {
            started: false,
            endTime: Date.now() + 3000,
            coordinates,
        },
    }));

const updateServiceTerm = (e: ChangeEvent<HTMLInputElement>) => {
    updateTerm(e.target.value);
};

const searchService = (serviceCode: string, { getMapId }: LayerConfig) =>
    queryService({
        service: serviceCode,
        input: getTerm(),
        lang: getLang(),
        mapId: serviceCode === 'layers' ? getMapId().toUndefined() : undefined,
    });

const zoomTo = (
    coords: Coordinate,
    setInteraction: Setter<Interaction>,
    setView: Setter<IMapViewData>,
) => {
    const viewEvents = viewEventsFactory(setView);
    clearServiceResponse();
    viewEvents.updateMapView({
        dirty: 'geo',
        center: coords,
        zoom: 12,
    });
    putMark(coords, setInteraction);
};

const tooltip = (actionName: string, result: TermResult): Tooltip => ({
    text: `${actionName}\n${serviceMessageToString(result.message)}` as Translated,
    position: 'bottom-right',
});

const centerTooltip = (result: TermResult) =>
    tooltip(tr.core('zoomToAddress'), result);

const centerButton = (result: TermResult) =>
    makeIcon('select', 3, 'map-marker-alt', centerTooltip(result));

const selectButton = (actionName: string, result: TermResult) =>
    makeIcon('select', 3, 'play', tooltip(actionName, result));

export const makeActionSelect =
    (actionName: string, action: (c: Coordinate) => void): TermAction =>
    result => {
        return selectButton(actionName, result)(() => action(coord(result)));
    };

export const tableButton = makeIcon('table', 3, 'table', {
    text: () => tr.core('tooltip:dataAccess'),
    position: 'right',
});

export const selectOnMapButton = makeIcon('select', 3, 'mouse-pointer', {
    text: () => tr.core('tooltip:selectOnMap'),
    position: 'right',
});

export interface LayerResultCount {
    layerId: string;
    metadataId: string;
    field: string;
    featureIds: number[] | string[];
}

export const countLayerResults = (results: LayerResult[]): LayerResultCount[] =>
    Array.from(
        results
            .reduce((acc, { layerId, metadataId, field, featureId }) => {
                const key = `${layerId}\0${field}`;
                const prev = acc.get(key) ?? {
                    layerId,
                    metadataId,
                    field,
                    featureIds: [],
                };
                return acc.set(key, {
                    ...prev,
                    // asserting because we all results will have the same type
                    // for featureId
                    featureIds: [...prev.featureIds, featureId] as
                        | number[]
                        | string[],
                });
            }, new Map<string, LayerResultCount>())
            .values(),
    );

const renderLayerResults = (
    results: LayerResult[],
    { onLabelClick, buttons, getDatasetMetadata }: LayerConfig,
) => {
    const layerName = (metadataId: string, layerId: string) =>
        getDatasetMetadata(metadataId).fold(layerId, md =>
            // todo: get alias for table
            fromRecord(getMessageRecord(md.resourceTitle)),
        );

    const fieldName = (metadataId: string, field: string) =>
        STRONG(
            'field',
            SPAN('fa picto', tryNameToString('arrow-right')),
            getDatasetMetadata(metadataId).fold(toTranslated(field), md =>
                getAlias(field, some(md.id)),
            ),
        );

    const searchQuery = getTerm();
    return countLayerResults(results).map(
        ({ layerId, metadataId, field, featureIds }, key) =>
            DIV(
                { className: 'result-select', key },
                DIV(
                    {
                        id: `geocoder-result-${key}`,
                        className: 'result-label',
                        onClick: () =>
                            onLabelClick(
                                layerId,
                                field,
                                searchQuery,
                                featureIds,
                            ),
                    },
                    SPAN('layer-name', layerName(metadataId, layerId)),
                    fieldName(metadataId, field),
                ),
                DIV(
                    'result-actions',
                    SPAN('count', `${featureIds.length}`),
                    ...buttons.map(action =>
                        action(layerId, field, searchQuery, featureIds),
                    ),
                ),
            ),
    );
};

const renderTermResult = (
    key: number,
    result: TermResult,
    setView: Setter<IMapViewData>,
    setInteraction: Setter<Interaction>,
    termActions: TermAction[] = [],
) =>
    DIV(
        { className: 'result-select', key },
        SPAN(
            {
                id: `geocoder-result-${key}`,
                className: 'result-label',
                onClick: () => zoomTo(coord(result), setInteraction, setView),
            },
            serviceMessageToString(result.message),
        ),
        DIV(
            'result-actions',
            centerButton(result)(() =>
                zoomTo(coord(result), setInteraction, setView),
            ),
            ...termActions.map(a => a(result)),
        ),
    );

const renderResults = (
    response: ServiceResponse,
    termConfig: TermConfig,
    layerConfig: LayerConfig,
) => {
    switch (response.kind) {
        case 'layers':
            return renderLayerResults(response.results, layerConfig);
        case 'term':
            return response.results.map((result, key) =>
                renderTermResult(
                    key,
                    result,
                    termConfig.setView,
                    termConfig.setInteraction,
                    termConfig.termActions,
                ),
            );
    }
};

const btnSearch = makeIcon('search', 1, 'search', {
    position: 'right',
    text: () => tr.core('search'),
});

export const renderService = (
    placeholder: string,
    serviceCode: string,
    termConfig: TermConfig,
    layerConfig: LayerConfig,
) =>
    DIV(
        'search-widget',
        DIV(
            'search',
            INPUT({
                type: 'text',
                name: 'adress',
                placeholder: `${placeholder}`,
                onChange: updateServiceTerm,
                onKeyPress: (e: KeyboardEvent<HTMLInputElement>) => {
                    if (isENTER(e)) {
                        searchService(serviceCode, layerConfig);
                    }
                },
            }),
            btnSearch(() => searchService(serviceCode, layerConfig)),
        ),
        getServiceResponse().map(r =>
            r.results.length > 0
                ? DIV(
                      'search-results',
                      renderResults(r, termConfig, layerConfig),
                  )
                : DIV('search-results', DIV('', tr.core('geocodeNoResults'))),
        ),
    );

const renderServiceList = renderSelect(
    `geocoder-service`,
    getServiceName,
    selectService,
    eqString,
);

export const renderServiceOrList = (services: ServiceDescription[]) => {
    switch (services.length) {
        case 0:
            return none;
        case 1:
            return some(DIV('mono', getServiceName(services[0].code)));
        default:
            return some(
                renderServiceList(
                    services.map(s => s.code),
                    getSelectedService(),
                ),
            );
    }
};

type TermConfig = {
    setView: Setter<IMapViewData>;
    setInteraction: Setter<Interaction>;
    termActions?: TermAction[];
};

export type LayerAction = (
    layerId: string,
    field: string,
    searchQuery: string,
    featureIds: number[] | string[],
) => NodeOrOptional;

type LayerConfig = {
    getMapId: Getter<Option<string>>;
    getDatasetMetadata: MetadataGetter;
    onLabelClick: (
        layerId: string,
        field: string,
        q: string,
        featureIds: number[] | string[],
    ) => void;
    buttons: LayerAction[];
};

export interface GeocoderConfig {
    termConfig: TermConfig;
    layerConfig?: LayerConfig;
}

export const layerConfigNoop: LayerConfig = {
    getMapId: () => none,
    getDatasetMetadata: () => none,
    onLabelClick: noop,
    buttons: [],
};

export const geocoder = (cfg: GeocoderConfig) =>
    DIV(
        'geocoder-wrapper',
        renderServiceOrList(getAllServices()),
        getSelectedService().map(service =>
            DIV(
                `search-wrapper service-${service}`,
                renderService(
                    getServicePlaceholder(service),
                    service,
                    cfg.termConfig,
                    cfg.layerConfig ?? layerConfigNoop,
                ),
            ),
        ),
    );

export const centerGeocoder = (
    setView: Setter<IMapViewData>,
    setInteraction: Setter<Interaction>,
) =>
    geocoder({
        termConfig: {
            setView,
            setInteraction,
        },
    });

export default geocoder;
